import { ErrorMessage } from "@/components/ErrorMessage";
import { Button } from "@/components/flexkit/Button";
import { useGetWorkflow, useUpdateWorkflow, type PlainWorkflow } from "@/queries/automation-queries";
import { DEFAULT_WORKFLOW_LIMITS, workflowDetailsUrl, workflowName, workflowsUrl } from "@/utils/automations";
import { useCallback, useEffect, useMemo, type FC } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BreadcrumbsSkeleton } from "@/components/BreadcrumbsSkeleton";
import { AutomationDefinitionSectionEdit } from "@/routes/automations/automation-edit-page/AutomationDefinitionSectionEdit";
import { SaveAutomationStepsButton } from "@/routes/automations/automation-edit-page/SaveAutomationStepsButton";
import { atom, useAtom } from "jotai";
import { atomEffect } from "jotai-effect";
import {
    convertWorkflowActionToFormSteps,
    type WorkflowEditFormStepData,
    type WorkflowEditFormStepType,
    type WorkflowEditFormReportOutputData,
    type WorkflowEditFormTriggerData,
    convertWorkflowActionToFormLimits,
    convertTriggersToFormData,
} from "@/components/automations/automation-edit-form-data";
import { newWorkflowFromTemplate, type AutomationTemplate } from "@/utils/automation-templates";
import CreateAutomationButton from "@/routes/automations/automation-edit-page/CreateAutomationButton";
import { AutomationEditableHeader } from "@/routes/automations/automation-edit-page/AutomationEditableHeader";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { v4 as uuidv4 } from "uuid";
import type { DraftStep, SidePanelEditingState } from "@/routes/automations/automation-edit-page/types";
import { useDocumentTitle } from "@/hooks/use-document-title";
import type { PlainMessage } from "@bufbuild/protobuf";
import type { Subject } from "gitpod-next-api/gitpod/v1/identity_pb";

/**
 * Creates all atoms needed for the edit page.
 * These atoms are scoped to this component instance.
 */
function useEditPageAtoms() {
    return useMemo(() => {
        // Core state atoms
        const currentWorkflowAtom = atom<PlainWorkflow | undefined>(undefined);
        const editableStepsAtom = atom<WorkflowEditFormStepData[] | null>(null);
        const editableTriggersAtom = atom<WorkflowEditFormTriggerData[]>([]);
        const editableExecutorAtom = atom<PlainMessage<Subject> | undefined>(undefined);
        const editableMaxParallelAtom = atom<number | undefined>(undefined);
        const editableMaxTotalAtom = atom<number | undefined>(undefined);
        const sidePanelEditingAtom = atom<SidePanelEditingState>(null);
        const newStepAtom = atom<WorkflowEditFormStepData | null>(null);
        const draftStepAtom = atom<DraftStep | null>(null);

        // Derived atom: is a new step being created
        const isCreatingStepAtom = atom((get) => {
            const editing = get(sidePanelEditingAtom);
            return editing === null && get(newStepAtom) !== null;
        });

        // Action atom: replace step at index
        const replaceStepAtom = atom(
            null,
            (get, set, { index, step }: { index: number; step: WorkflowEditFormStepData }) => {
                const steps = get(editableStepsAtom);
                if (steps) {
                    set(editableStepsAtom, [...steps.slice(0, index), step, ...steps.slice(index + 1)]);
                }
            },
        );

        // Action atom: remove step at index
        const removeStepAtom = atom(null, (get, set, index: number) => {
            const steps = get(editableStepsAtom);
            if (steps) {
                set(
                    editableStepsAtom,
                    steps.filter((_, i) => i !== index),
                );
                const editing = get(sidePanelEditingAtom);
                if (editing?.type === "step") {
                    if (index < editing.index) {
                        set(sidePanelEditingAtom, { type: "step", index: editing.index - 1 });
                    } else if (index === editing.index) {
                        set(sidePanelEditingAtom, null);
                    }
                }
            }
        });

        // Action atom: create new step
        const createNewStepAtom = atom(null, (_get, set, type: WorkflowEditFormStepType = "command") => {
            set(sidePanelEditingAtom, null);
            switch (type) {
                case "prompt":
                case "command":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        content: "",
                    });
                    break;
                case "pr":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        title: "",
                        description: "",
                        branch: "",
                        isDraft: false,
                    });
                    break;
                case "report":
                    set(newStepAtom, {
                        id: uuidv4(),
                        type,
                        outputs: [],
                    });
                    break;
            }
        });

        // Effect: create step if empty
        const createStepIfEmptyAtomEffect = atomEffect((get, set) => {
            const steps = get(editableStepsAtom);
            if (steps && steps.length === 0) {
                set(createNewStepAtom, "command");
            }
        });

        // Effect: create draft from selected step or new step
        const createDraftAtomEffect = atomEffect((get, set) => {
            const editing = get(sidePanelEditingAtom);
            const newStep = get(newStepAtom);
            const step = editing?.type === "step" ? get(editableStepsAtom)?.[editing.index] : null;
            const sourceStep = step ?? newStep;
            console.log({ sourceStep, newStep, step });
            if (sourceStep) {
                switch (sourceStep.type) {
                    case "prompt":
                    case "command":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: sourceStep.type,
                            content: atom(sourceStep.content),
                        });
                        break;
                    case "pr":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: "pr",
                            title: atom(sourceStep.title),
                            description: atom(sourceStep.description),
                            branch: atom(sourceStep.branch),
                            isDraft: atom(sourceStep.isDraft),
                        });
                        break;
                    case "report":
                        set(draftStepAtom, {
                            id: sourceStep.id,
                            type: "report",
                            outputs: atom(sourceStep.outputs),
                        });
                        break;
                }
            } else {
                set(draftStepAtom, null);
            }
        });

        // Track if initial sync has happened
        const initialSyncDoneAtom = atom(false);

        // Effect: sync workflow to editable atoms
        const syncWorkflowToEditableAtomsEffect = atomEffect((get, set) => {
            const workflow = get(currentWorkflowAtom);
            if (!workflow) {
                return;
            }

            // Only sync once on initial load
            const syncDone = get(initialSyncDoneAtom);
            if (syncDone) {
                return;
            }

            // Sync steps
            if (workflow.spec?.action) {
                set(editableStepsAtom, convertWorkflowActionToFormSteps(workflow.spec.action));
            } else {
                set(editableStepsAtom, []);
            }

            // Sync triggers
            if (workflow.spec?.triggers?.[0]) {
                set(editableTriggersAtom, convertTriggersToFormData(workflow.spec.triggers));
            } else {
                set(editableTriggersAtom, []);
            }

            // Sync executor
            set(editableExecutorAtom, workflow.metadata?.executor);

            // Sync limits
            const limits = convertWorkflowActionToFormLimits(workflow.spec?.action);
            set(editableMaxParallelAtom, limits?.maxParallel ?? DEFAULT_WORKFLOW_LIMITS.maxParallel);
            set(editableMaxTotalAtom, limits?.maxTotal ?? DEFAULT_WORKFLOW_LIMITS.maxTotal);

            set(initialSyncDoneAtom, true);
        });

        // Action atom: change draft step type
        const changeDraftStepTypeAtom = atom(null, (get, set, type: WorkflowEditFormStepType) => {
            const draftStep = get(draftStepAtom);
            if (!draftStep) {
                return;
            }

            const currentType = draftStep.type;
            const canPreserveContent =
                (currentType === "prompt" || currentType === "command") && (type === "prompt" || type === "command");

            if (canPreserveContent) {
                set(draftStepAtom, {
                    ...draftStep,
                    type,
                });
            } else {
                switch (type) {
                    case "command":
                    case "prompt":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            content: atom(""),
                        });
                        break;
                    case "pr":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            title: atom(""),
                            description: atom(""),
                            branch: atom(""),
                            isDraft: atom(false),
                        });
                        break;
                    case "report":
                        set(draftStepAtom, {
                            id: draftStep.id,
                            type,
                            outputs: atom<WorkflowEditFormReportOutputData[]>([]),
                        });
                        break;
                }
            }
        });

        // Action atom: upsert draft step into steps
        const upsertDraftStepIntoStepsAtom = atom(null, (get, set) => {
            const draftStep = get(draftStepAtom);
            if (!draftStep) {
                return;
            }

            let newStep: WorkflowEditFormStepData;
            switch (draftStep.type) {
                case "command":
                case "prompt":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        content: get(draftStep.content),
                    };
                    break;
                case "pr":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        title: get(draftStep.title),
                        description: get(draftStep.description),
                        branch: get(draftStep.branch),
                        isDraft: get(draftStep.isDraft),
                    };
                    break;
                case "report":
                    newStep = {
                        id: draftStep.id,
                        type: draftStep.type,
                        outputs: get(draftStep.outputs),
                    };
                    break;
            }

            const editing = get(sidePanelEditingAtom);

            if (editing?.type === "step") {
                set(replaceStepAtom, { index: editing.index, step: newStep });
                set(sidePanelEditingAtom, null);
                return false;
            } else {
                const steps = get(editableStepsAtom);
                set(editableStepsAtom, steps ? [...steps, newStep] : [newStep]);
                set(createNewStepAtom, "command");
                return true;
            }
        });

        return {
            currentWorkflowAtom,
            editableStepsAtom,
            editableTriggersAtom,
            editableExecutorAtom,
            editableMaxParallelAtom,
            editableMaxTotalAtom,
            sidePanelEditingAtom,
            newStepAtom,
            draftStepAtom,
            isCreatingStepAtom,
            removeStepAtom,
            createNewStepAtom,
            createStepIfEmptyAtomEffect,
            createDraftAtomEffect,
            changeDraftStepTypeAtom,
            upsertDraftStepIntoStepsAtom,
            syncWorkflowToEditableAtomsEffect,
        };
    }, []);
}

type AutomationEditPageProps = {
    template?: AutomationTemplate;
};

export const AutomationEditPage: FC<AutomationEditPageProps> = ({ template }) => {
    const navigate = useNavigate();
    const { toast } = useToast();

    const { workflowId } = useParams<{ workflowId: string }>();
    const { data: workflowData, error: workflowError } = useGetWorkflow(template ? undefined : workflowId);
    const { mutateAsync: updateWorkflow } = useUpdateWorkflow();

    const atoms = useEditPageAtoms();
    const [workflow, setWorkflow] = useAtom(atoms.currentWorkflowAtom);

    // Set document title based on whether we're editing an existing workflow or creating a new one
    const documentTitle = template ? undefined : workflow ? `Edit ${workflowName(workflow)}` : undefined;
    useDocumentTitle(documentTitle);

    // Mount atom effect to sync workflow to editable atoms
    useAtom(atoms.syncWorkflowToEditableAtomsEffect);

    useEffect(() => {
        if (template) {
            setWorkflow(newWorkflowFromTemplate(template));
        } else if (workflowId) {
            setWorkflow(workflowData);
        }
    }, [workflowData, template, workflowId, setWorkflow]);

    const handleCancel = useCallback(() => {
        if (template) {
            navigate(workflowsUrl());
        }
        if (workflowId) {
            navigate(workflowDetailsUrl({ id: workflowId }));
        }
    }, [navigate, template, workflowId]);

    const handleHeaderUpdate = useCallback(
        async (name: string, description: string) => {
            if (template && workflow) {
                setWorkflow({
                    ...workflow,
                    metadata: {
                        ...workflow.metadata,
                        name,
                        description,
                    },
                });
            } else if (workflowId) {
                try {
                    await updateWorkflow({
                        workflowId,
                        name,
                        description,
                    });
                } catch (error) {
                    toast({
                        title: "Failed to update automation header",
                        description: formatError(error),
                    });
                    throw error;
                }
            }
        },
        [template, workflow, workflowId, updateWorkflow, toast, setWorkflow],
    );

    return (
        <div data-testid="workflow-edit-page" className="flex size-full flex-col">
            <div className="flex flex-col p-[26px]">
                {workflow ? (
                    <AutomationEditableHeader
                        workflow={workflow}
                        onUpdate={handleHeaderUpdate}
                        actions={
                            <div className="flex items-center gap-2">
                                <Button variant="outline" onClick={handleCancel} data-tracking-id="cancel">
                                    Cancel
                                </Button>
                                {template ? (
                                    <CreateAutomationButton
                                        workflow={workflow}
                                        editableStepsAtom={atoms.editableStepsAtom}
                                    />
                                ) : (
                                    <SaveAutomationStepsButton
                                        workflowId={workflow.id}
                                        editableStepsAtom={atoms.editableStepsAtom}
                                        editableTriggersAtom={atoms.editableTriggersAtom}
                                        editableExecutorAtom={atoms.editableExecutorAtom}
                                        editableMaxParallelAtom={atoms.editableMaxParallelAtom}
                                        editableMaxTotalAtom={atoms.editableMaxTotalAtom}
                                    />
                                )}
                            </div>
                        }
                    />
                ) : (
                    <BreadcrumbsSkeleton itemsCount={2} />
                )}
                <ErrorMessage error={workflowError} />
            </div>

            {workflow && (
                <AutomationDefinitionSectionEdit
                    workflow={workflow}
                    editableStepsAtom={atoms.editableStepsAtom}
                    editableTriggersAtom={atoms.editableTriggersAtom}
                    editableExecutorAtom={atoms.editableExecutorAtom}
                    editableMaxParallelAtom={atoms.editableMaxParallelAtom}
                    editableMaxTotalAtom={atoms.editableMaxTotalAtom}
                    sidePanelEditingAtom={atoms.sidePanelEditingAtom}
                    newStepAtom={atoms.newStepAtom}
                    draftStepAtom={atoms.draftStepAtom}
                    isCreatingStepAtom={atoms.isCreatingStepAtom}
                    removeStepAtom={atoms.removeStepAtom}
                    createNewStepAtom={atoms.createNewStepAtom}
                    createStepIfEmptyAtomEffect={atoms.createStepIfEmptyAtomEffect}
                    createDraftAtomEffect={atoms.createDraftAtomEffect}
                    changeDraftStepTypeAtom={atoms.changeDraftStepTypeAtom}
                    upsertDraftStepIntoStepsAtom={atoms.upsertDraftStepIntoStepsAtom}
                />
            )}
        </div>
    );
};
